import { useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowLeft, ArrowUp, ClipboardPaste, Plus } from 'lucide-react';
import {
  createChoiceValue,
  deleteChoiceList,
  pasteChoiceValues,
  reorderChoiceList,
  updateChoiceValue,
} from '@mkcore/platform/schema';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ErrorState,
  Input,
  Mono,
  PageHeader,
  Skeleton,
  Switch,
  TBody,
  THead,
  Table,
  Td,
  Textarea,
  Th,
  Tr,
  toast,
} from '@mkcore/ui';
import { newIdempotencyKey } from '@/lib/api';
import { useActionMutation, useErrorMessage } from '@/lib/mutation';
import { useCan } from '@/lib/session';
import { choiceListQuery } from '@/features/admin/queries';
import { ConfirmButton } from '@/components/confirm';

export const Route = createFileRoute('/_app/admin/choice-lists/$id')({ component: ChoiceListDetail });

/** One choice list: rename values inline, switch them off, reorder with the keyboard and paste in bulk (CO-0039). */
function ChoiceListDetail() {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const list = useQuery(choiceListQuery(id));
  const canUpdate = useCan('admin:update');
  const canDelete = useCan('admin:delete');
  const message = useErrorMessage();
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const inv = [['admin', 'choiceList']];
  const update = useActionMutation(updateChoiceValue, { invalidate: inv });
  const reorder = useActionMutation(reorderChoiceList, { invalidate: inv });
  const add = useActionMutation(createChoiceValue, { invalidate: inv, onSuccess: () => setNewLabel('') });
  const paste = useActionMutation(pasteChoiceValues, {
    invalidate: inv,
    onSuccess: (r) => {
      toast.success(t('choiceLists.pasted', { added: r.added, skipped: r.skipped }));
      setPasteOpen(false);
      setPasteText('');
    },
  });
  const remove = useActionMutation(deleteChoiceList, { invalidate: inv, onSuccess: () => void navigate({ to: '/admin/choice-lists' }) });

  if (list.isError) return <ErrorState title={t('errors.generic')} description={message(list.error)} />;
  if (!list.data) return <Skeleton className="h-96" />;
  const l = list.data;
  const ids = l.values.map((v) => v.id);
  const move = (index: number, delta: number) => {
    const next = [...ids];
    const [item] = next.splice(index, 1);
    next.splice(index + delta, 0, item!);
    reorder.mutate({ input: { id, valueIds: next } });
  };

  return (
    <>
      <Link to="/admin/choice-lists" className="mb-3 inline-flex items-center gap-1 text-sm text-fg-2 hover:text-fg-1">
        <ArrowLeft className="size-4" /> {t('choiceLists.title')}
      </Link>
      <PageHeader
        eyebrow={<Mono>{l.key}</Mono>}
        title={l.name}
        description={l.description}
        actions={
          <>
            {canUpdate && (
              <Button variant="secondary" onClick={() => setPasteOpen(true)}>
                <ClipboardPaste /> {t('choiceLists.paste')}
              </Button>
            )}
            {canDelete && (
              <ConfirmButton
                variant="danger"
                label={t('common.delete')}
                title={t('choiceLists.deleteConfirm', { name: l.name })}
                onConfirm={() => remove.mutateAsync({ input: { id }, ifMatch: l.version })}
              />
            )}
          </>
        }
      />
      <Table>
        <THead>
          <Tr>
            <Th>{t('choiceLists.label')}</Th>
            <Th>{t('choiceLists.value')}</Th>
            <Th>{t('choiceLists.active')}</Th>
            <Th className="sr-only">{t('common.actions')}</Th>
          </Tr>
        </THead>
        <TBody>
          {l.values.map((v, i) => (
            <Tr key={v.id}>
              <Td>
                <Input
                  aria-label={t('choiceLists.label')}
                  defaultValue={v.label}
                  disabled={!canUpdate}
                  className="h-8 max-w-xs"
                  onBlur={(e) => {
                    const label = e.target.value.trim();
                    if (label && label !== v.label) update.mutate({ input: { id: v.id, label }, ifMatch: v.version });
                  }}
                  onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
                />
              </Td>
              <Td>
                <Mono className="text-xs">{v.value}</Mono>
              </Td>
              <Td>
                <Switch
                  checked={v.active}
                  disabled={!canUpdate}
                  aria-label={t('choiceLists.active')}
                  onCheckedChange={(active) => update.mutate({ input: { id: v.id, active }, ifMatch: v.version })}
                />
              </Td>
              <Td className="whitespace-nowrap text-right">
                {canUpdate && (
                  <>
                    <Button size="icon" variant="ghost" aria-label={t('common.moveUp')} disabled={i === 0} onClick={() => move(i, -1)}>
                      <ArrowUp />
                    </Button>
                    <Button size="icon" variant="ghost" aria-label={t('common.moveDown')} disabled={i === ids.length - 1} onClick={() => move(i, 1)}>
                      <ArrowDown />
                    </Button>
                  </>
                )}
              </Td>
            </Tr>
          ))}
        </TBody>
      </Table>
      {canUpdate && (
        <form
          className="mt-4 flex max-w-md gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (newLabel.trim()) add.mutate({ input: { listId: id, label: newLabel.trim() }, key: newIdempotencyKey() });
          }}
        >
          <Input aria-label={t('choiceLists.addValue')} placeholder={t('choiceLists.addValue')} value={newLabel} onChange={(e) => setNewLabel(e.target.value)} />
          <Button type="submit" variant="secondary" loading={add.isPending}>
            <Plus /> {t('common.add')}
          </Button>
        </form>
      )}
      <Dialog open={pasteOpen} onOpenChange={setPasteOpen}>
        <DialogContent closeLabel={t('common.close')}>
          <DialogHeader>
            <DialogTitle>{t('choiceLists.pasteTitle')}</DialogTitle>
            <DialogDescription>{t('choiceLists.pasteHint')}</DialogDescription>
          </DialogHeader>
          <Textarea aria-label={t('choiceLists.values')} className="min-h-48" value={pasteText} onChange={(e) => setPasteText(e.target.value)} />
          <DialogFooter>
            <Button variant="secondary" onClick={() => setPasteOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button loading={paste.isPending} onClick={() => paste.mutate({ input: { id, text: pasteText } })}>
              {t('choiceLists.paste')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
